import { useCallback, useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canOwnerWrite } from "../lib/permissions";
import {
  Stethoscope, Plus, X, Save, Pencil, Trash2, Upload, FileText, Receipt,
  ChevronDown, ChevronRight, AlertTriangle,
} from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

// ---------- Permission helpers (mirror backend ENH-010b) ----------
const NO_ACCESS_ROLES = new Set(["driver"]);
const CREATE_ROLES = new Set(["super_admin", "admin", "manager", "vet"]);
const DELETE_ROLES = new Set(["super_admin", "admin", "manager"]);
// ENH-vet-1: manager stays out of health, invoice, and cost views entirely.
const HEALTH_FILE_WRITE_ROLES = new Set(["super_admin", "admin", "vet", "owner"]);
const INVOICE_FILE_WRITE_ROLES = new Set(["super_admin", "admin", "owner"]);
const RECOVERY_EDIT_ROLES = new Set(["super_admin", "admin", "manager", "owner"]);

const REASONS = [
  { value: "routine", label: "Routine" },
  { value: "injury", label: "Injury" },
  { value: "illness", label: "Illness" },
  { value: "pre_competition", label: "Pre-competition" },
  { value: "follow_up", label: "Follow-up" },
  { value: "other", label: "Other" },
];
const REASON_LABEL = Object.fromEntries(REASONS.map((r) => [r.value, r.label]));

const RESTRICTIONS = [
  { value: "box_rest", label: "Box rest" },
  { value: "walker_only", label: "Walker only" },
  { value: "no_jumping", label: "No jumping" },
  { value: "no_paddock", label: "No paddock" },
  { value: "light_work_only", label: "Light work only" },
  { value: "hand_walk", label: "Hand walk" },
  { value: "lunging", label: "Lunging" },
  { value: "other", label: "Other" },
];
const RESTRICTION_LABEL = Object.fromEntries(RESTRICTIONS.map((r) => [r.value, r.label]));

const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; } };
const fmtMoney = (n) => n == null ? "—" : `€${Number(n).toFixed(2)}`;
const todayIso = () => new Date().toISOString().slice(0, 10);
const isActive = (r) => { const t = todayIso(); return r.start_date <= t && t <= r.end_date; };
const shortName = (name, max = 32) => (name && name.length > max ? `${name.slice(0, max - 1)}…` : name);

// Server strips keys entirely for restricted roles.
// health_report_files / invoice_files === undefined → hide the whole block.
const hasKey = (obj, k) => Object.prototype.hasOwnProperty.call(obj || {}, k);

export default function VetVisits({ horseId, horseName, onRestrictionsChanged }) {
  const { user } = useAuth();
  const role = user?.role;
  const canView = !NO_ACCESS_ROLES.has(role);
  // ENH-015: owner may now create vet visits on their own horse (backend enforces horse-ownership).
  const canCreate = CREATE_ROLES.has(role) || canOwnerWrite(role);

  const [visits, setVisits] = useState(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get(`/horses/${horseId}/vet-visits`);
      setVisits(data);
    } catch (e) { setError(formatApiError(e)); setVisits([]); }
  }, [horseId]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const toggleExpand = (id) => {
    const n = new Set(expanded);
    if (n.has(id)) n.delete(id); else n.add(id);
    setExpanded(n);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/horses/${horseId}/vet-visits/${confirmDel.id}`);
      setConfirmDel(null);
      await load();
      if (onRestrictionsChanged) onRestrictionsChanged();
    } catch (e) { setError(formatApiError(e)); setConfirmDel(null); }
  };

  if (!canView) return null;

  return (
    <div className="se-card" data-testid="vet-visits-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="se-label flex items-center gap-2"><Stethoscope size={12} /> Health</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 22 }}>Vet visits</h3>
        </div>
        {canCreate && (
          <button className="se-btn" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="vet-visit-add-button">
            <Plus size={14} /> Add vet visit
          </button>
        )}
      </div>
      {error && <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="vet-visits-error">{error}</div>}

      {visits === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
      {visits && visits.length === 0 && (
        <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="vet-visits-empty">
          No vet visits logged yet.
        </div>
      )}

      <div className="space-y-2">
        {(visits || []).map((v) => {
          const activeRs = (v.recovery_schedules || []).filter(isActive);
          const canEditThis = ["super_admin", "admin", "manager"].includes(role) || (role === "vet" && v.created_by === user?.id) || (role === "owner");
          const canDeleteThis = DELETE_ROLES.has(role);
          const isOpen = expanded.has(v.id);
          return (
            <div key={v.id} className="border rounded-lg" style={{ borderColor: "rgba(212,169,106,0.25)" }} data-testid={`vet-visit-row-${v.id}`}>
              <button type="button" onClick={() => toggleExpand(v.id)} className="w-full flex items-start gap-3 p-3 text-left" style={{ cursor: "pointer" }} data-testid={`vet-visit-toggle-${v.id}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(45,110,110,0.12)", color: "#2D6E6E" }}>
                  <Stethoscope size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span style={{ fontWeight: 600 }}>{REASON_LABEL[v.reason]}</span>
                    <span className="text-xs" style={{ color: "#8C9E8A" }}>· {fmtDate(v.date)}</span>
                    {activeRs.length > 0 && (
                      <span className="se-badge" style={{ background: "rgba(192,57,43,0.14)", color: "#922B21" }}>
                        <AlertTriangle size={10} style={{ display: "inline", marginRight: 3 }} />
                        {activeRs.length} active restriction{activeRs.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>
                    Vet: {v.vet_name}
                    {hasKey(v, "cost") && v.cost != null ? ` · ${fmtMoney(v.cost)}` : ""}
                    {v.next_appointment_date ? ` · Next: ${fmtDate(v.next_appointment_date)}` : ""}
                    {v.created_by_name ? ` · Logged by ${v.created_by_name}` : ""}
                  </div>
                </div>
                {isOpen ? <ChevronDown size={16} style={{ color: "#8C9E8A", marginTop: 8 }} /> : <ChevronRight size={16} style={{ color: "#8C9E8A", marginTop: 8 }} />}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-3 border-t pt-3" style={{ borderColor: "rgba(212,169,106,0.2)" }} data-testid={`vet-visit-detail-${v.id}`}>
                  {v.notes && (
                    <div>
                      <div className="se-label mb-1">Notes</div>
                      <p className="text-sm whitespace-pre-wrap">{v.notes}</p>
                    </div>
                  )}

                  {hasKey(v, "health_report_files") && (
                    <VisitFileList
                      horseId={horseId}
                      visitId={v.id}
                      kind="health"
                      files={v.health_report_files || []}
                      role={role}
                      icon={<FileText size={14} />}
                      label="Health reports"
                      testidPrefix={`vet-visit-health-${v.id}`}
                      onChanged={load}
                    />
                  )}
                  {hasKey(v, "invoice_files") && (
                    <VisitFileList
                      horseId={horseId}
                      visitId={v.id}
                      kind="invoice"
                      files={v.invoice_files || []}
                      role={role}
                      icon={<Receipt size={14} />}
                      label="Invoices"
                      testidPrefix={`vet-visit-invoice-${v.id}`}
                      onChanged={load}
                    />
                  )}

                  {v.recovery_schedules && v.recovery_schedules.length > 0 && (
                    <div>
                      <div className="se-label mb-2">Recovery schedule</div>
                      <div className="space-y-1">
                        {v.recovery_schedules.map((r, idx) => {
                          const active = isActive(r);
                          return (
                            <div
                              key={idx}
                              className="p-2 rounded-lg flex items-center gap-2 text-sm"
                              style={{
                                background: active ? "rgba(192,57,43,0.08)" : "rgba(212,169,106,0.06)",
                                border: active ? "0.5px solid rgba(192,57,43,0.3)" : "0.5px solid rgba(212,169,106,0.25)",
                              }}
                              data-testid={`vet-visit-restriction-${v.id}-${idx}`}
                            >
                              <span style={{ fontWeight: 600, color: active ? "#922B21" : "#3B2F1E" }}>{RESTRICTION_LABEL[r.restriction_type] || r.restriction_type}</span>
                              <span style={{ color: "#8C9E8A" }}>· {fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                              {r.restriction_type === "hand_walk" && (r.times_per_day != null || r.duration_minutes != null) && (
                                <span className="text-xs" style={{ color: "#5B6B5B" }} data-testid={`vet-visit-restriction-${v.id}-${idx}-handwalk`}>
                                  · {r.times_per_day != null ? `${r.times_per_day}×/day` : ""}
                                  {r.times_per_day != null && r.duration_minutes != null ? " · " : ""}
                                  {r.duration_minutes != null ? `${r.duration_minutes} min` : ""}
                                </span>
                              )}
                              {active && <span className="se-badge se-badge-urgent" style={{ marginLeft: "auto" }}>Active</span>}
                              {r.notes && <span className="text-xs w-full mt-1" style={{ color: "#5B6B5B" }}>{r.notes}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {(canEditThis || canDeleteThis) && (
                    <div className="flex gap-2 justify-end pt-1">
                      {canEditThis && (
                        <button className="se-btn se-btn-ghost" style={{ padding: "6px 10px" }} onClick={() => { setEditing(v); setShowForm(true); }} data-testid={`vet-visit-edit-${v.id}`}>
                          <Pencil size={12} /> Edit
                        </button>
                      )}
                      {canDeleteThis && (
                        <button className="se-btn se-btn-ghost" style={{ padding: "6px 10px", color: "#C0392B" }} onClick={() => setConfirmDel(v)} data-testid={`vet-visit-delete-${v.id}`}>
                          <Trash2 size={12} /> Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showForm && (
        <VetVisitFormModal
          horseId={horseId}
          horseName={horseName}
          role={role}
          existing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { setShowForm(false); setEditing(null); await load(); if (onRestrictionsChanged) onRestrictionsChanged(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={doDelete}
        title="Delete this vet visit?"
        description="This visit and its recovery schedule will be moved to trash."
        confirmLabel="Yes, delete"
        testid="vet-visit-delete-confirm"
      />
    </div>
  );
}

// ---------- File list block (used both in detail view and inside the edit form) ----------
function VisitFileList({ horseId, visitId, kind, files, role, icon, label, testidPrefix, onChanged, embedded = false }) {
  const canWrite = kind === "health" ? HEALTH_FILE_WRITE_ROLES.has(role) : INVOICE_FILE_WRITE_ROLES.has(role);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const endpoint = kind === "health" ? "health-reports" : "invoices";

  const uploadOne = async (file) => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/horses/${horseId}/vet-visits/${visitId}/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (onChanged) await onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadMany = async (fileList) => {
    for (const f of Array.from(fileList || [])) {
      await uploadOne(f);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    if (!canWrite) return;
    if (e.dataTransfer?.files?.length) await uploadMany(e.dataTransfer.files);
  };

  const removeOne = async (fileId) => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/horses/${horseId}/vet-visits/${visitId}/${endpoint}/${fileId}`);
      if (onChanged) await onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center justify-between mb-1">
        <div className="se-label flex items-center gap-1">{icon} {label} <span style={{ color: "#8C9E8A", fontWeight: 400 }}>({files.length})</span></div>
      </div>

      {files.length === 0 && !canWrite && (
        <div className="text-xs" style={{ color: "#8C9E8A" }} data-testid={`${testidPrefix}-empty`}>None uploaded.</div>
      )}

      {files.length > 0 && (
        <ul className="space-y-1 mb-2" data-testid={`${testidPrefix}-list`}>
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-sm p-2 rounded-lg" style={{ background: "rgba(212,169,106,0.06)", border: "0.5px solid rgba(212,169,106,0.2)" }} data-testid={`${testidPrefix}-item-${f.id}`}>
              {icon}
              <a className="se-link truncate flex-1" href={f.file_url?.startsWith("http") ? f.file_url : `${BACKEND_URL}${f.file_url}`} target="_blank" rel="noreferrer" title={f.file_name}>{shortName(f.file_name, embedded ? 26 : 40)}</a>
              {f.uploaded_by_name && <span className="text-xs hidden sm:inline" style={{ color: "#8C9E8A" }}>· {f.uploaded_by_name}</span>}
              {canWrite && (
                <button
                  type="button"
                  className="se-btn se-btn-ghost"
                  style={{ padding: 4, color: "#C0392B" }}
                  onClick={() => removeOne(f.id)}
                  disabled={busy}
                  aria-label="Remove"
                  data-testid={`${testidPrefix}-remove-${f.id}`}
                >
                  <X size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canWrite && (
        <label
          className="flex items-center justify-center gap-2 text-sm rounded-lg cursor-pointer"
          style={{
            border: `1px dashed ${dragOver ? "#7C5C3A" : "rgba(124,92,58,0.4)"}`,
            background: dragOver ? "rgba(124,92,58,0.08)" : "rgba(212,169,106,0.04)",
            padding: "10px 12px",
            color: "#5B4527",
          }}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid={`${testidPrefix}-dropzone`}
        >
          <Upload size={14} />
          <span>{busy ? "Uploading…" : `Drag files here or click to upload ${label.toLowerCase()}`}</span>
          <input
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files?.length) { uploadMany(e.target.files); e.target.value = ""; } }}
            data-testid={`${testidPrefix}-input`}
          />
        </label>
      )}

      {err && <div className="text-xs mt-1" style={{ color: "#922B21" }} data-testid={`${testidPrefix}-error`}>{err}</div>}
    </div>
  );
}

function VetVisitFormModal({ horseId, horseName, role, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const canEditRecovery = RECOVERY_EDIT_ROLES.has(role);
  // Files: only shown for existing visits (need a visit_id to POST files against)
  const showHealthFiles = isEdit && hasKey(existing, "health_report_files");
  const showInvoiceFiles = isEdit && hasKey(existing, "invoice_files");
  const canSeeCost = existing ? hasKey(existing, "cost") : (role === "super_admin" || role === "admin" || role === "owner");

  const [form, setForm] = useState(() => existing ? ({
    date: existing.date,
    event_time: existing.event_time || "",
    next_appointment_date: existing.next_appointment_date || "",
    vet_name: existing.vet_name,
    reason: existing.reason,
    notes: existing.notes || "",
    cost: (hasKey(existing, "cost") && existing.cost != null) ? String(existing.cost) : "",
  }) : ({
    date: todayIso(), event_time: "", next_appointment_date: "", vet_name: "",
    reason: "routine", notes: "", cost: "",
  }));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [localVisit, setLocalVisit] = useState(existing);

  const refreshVisit = async () => {
    if (!isEdit) return;
    try {
      const { data } = await api.get(`/horses/${horseId}/vet-visits`);
      const fresh = (data || []).find((x) => x.id === existing.id);
      if (fresh) setLocalVisit(fresh);
    } catch (_) { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {};
      payload.date = form.date;
      payload.event_time = form.event_time || null;
      payload.next_appointment_date = form.next_appointment_date || null;
      payload.vet_name = form.vet_name.trim();
      payload.reason = form.reason;
      payload.notes = form.notes.trim() || null;
      if (canSeeCost) {
        payload.cost = form.cost !== "" ? Number(form.cost) : null;
      }
      if (canEditRecovery) {
        payload.recovery_schedules = (localVisit?.recovery_schedules || existing?.recovery_schedules || []).map((r) => {
          const row = {
            restriction_type: r.restriction_type,
            start_date: r.start_date,
            end_date: r.end_date,
            notes: (r.notes || "").trim() || null,
          };
          if (r.restriction_type === "hand_walk") {
            row.times_per_day = r.times_per_day != null && r.times_per_day !== "" ? Number(r.times_per_day) : null;
            row.duration_minutes = r.duration_minutes != null && r.duration_minutes !== "" ? Number(r.duration_minutes) : null;
          }
          return row;
        });
      }
      if (isEdit) await api.patch(`/horses/${horseId}/vet-visits/${existing.id}`, payload);
      else await api.post(`/horses/${horseId}/vet-visits`, payload);
      await onSaved();
    } catch (e2) { setErr(e2?.response?.data?.detail || e2.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const recoverySchedules = localVisit?.recovery_schedules ?? existing?.recovery_schedules ?? [];
  const updateRecovery = (next) => setLocalVisit((v) => ({ ...(v || existing || {}), recovery_schedules: next }));
  const addRestriction = () => updateRecovery([...recoverySchedules, { restriction_type: "box_rest", start_date: todayIso(), end_date: todayIso(), notes: "" }]);
  const updateRestriction = (idx, patch) => {
    const next = [...recoverySchedules]; next[idx] = { ...next[idx], ...patch }; updateRecovery(next);
  };
  const removeRestriction = (idx) => updateRecovery(recoverySchedules.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={onClose}>
      <form className="se-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="vet-visit-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 20 }}>{isEdit ? "Edit vet visit" : `New vet visit — ${horseName || ""}`}</h3>
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={14} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Date *</label>
            <input type="date" className="se-input" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="vet-visit-date" />
          </div>
          <div>
            <label className="se-label block mb-1">Time (optional)</label>
            <input type="time" className="se-input" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} data-testid="vet-visit-time" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Next appt</label>
            <input type="date" className="se-input" value={form.next_appointment_date} onChange={(e) => setForm({ ...form, next_appointment_date: e.target.value })} data-testid="vet-visit-next-date" />
          </div>
        </div>
        <label className="se-label block mb-1">Vet name *</label>
        <input className="se-input mb-3" required value={form.vet_name} onChange={(e) => setForm({ ...form, vet_name: e.target.value })} data-testid="vet-visit-vet-name" />
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="se-label block mb-1">Reason</label>
            <select className="se-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="vet-visit-reason">
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {canSeeCost && (
            <div>
              <label className="se-label block mb-1">Cost (€)</label>
              <input type="number" min="0" step="0.01" className="se-input" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="vet-visit-cost" />
            </div>
          )}
        </div>
        <label className="se-label block mb-1">Notes</label>
        <textarea className="se-input mb-3" style={{ height: 70, padding: "10px 14px" }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="vet-visit-notes" />

        {!isEdit && (
          <div className="text-xs mb-3 p-2 rounded" style={{ background: "rgba(212,169,106,0.08)", color: "#5B4527" }} data-testid="vet-visit-files-hint">
            Save the visit first, then reopen it to attach health reports and invoices.
          </div>
        )}

        {showHealthFiles && (
          <div className="mb-3">
            <VisitFileList
              horseId={horseId}
              visitId={existing.id}
              kind="health"
              files={localVisit?.health_report_files || existing.health_report_files || []}
              role={role}
              icon={<FileText size={14} />}
              label="Health reports"
              testidPrefix={`vet-visit-form-health-${existing.id}`}
              onChanged={refreshVisit}
              embedded
            />
          </div>
        )}
        {showInvoiceFiles && (
          <div className="mb-3">
            <VisitFileList
              horseId={horseId}
              visitId={existing.id}
              kind="invoice"
              files={localVisit?.invoice_files || existing.invoice_files || []}
              role={role}
              icon={<Receipt size={14} />}
              label="Invoices"
              testidPrefix={`vet-visit-form-invoice-${existing.id}`}
              onChanged={refreshVisit}
              embedded
            />
          </div>
        )}

        {canEditRecovery ? (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="se-label">Recovery schedule</label>
              <button type="button" className="se-btn se-btn-ghost" style={{ padding: "4px 10px", fontSize: 13 }} onClick={addRestriction} data-testid="vet-visit-add-restriction">
                <Plus size={12} /> Add restriction
              </button>
            </div>
            {recoverySchedules.length === 0 ? (
              <div className="text-xs" style={{ color: "#8C9E8A" }}>No restrictions.</div>
            ) : (
              <div className="space-y-2">
                {recoverySchedules.map((r, idx) => (
                  <div key={idx} className="p-2 rounded-lg space-y-2" style={{ background: "rgba(212,169,106,0.06)", border: "0.5px solid rgba(212,169,106,0.25)" }} data-testid={`vet-visit-restriction-row-${idx}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <select className="se-input" value={r.restriction_type} onChange={(e) => updateRestriction(idx, { restriction_type: e.target.value })} data-testid={`vet-visit-restriction-type-${idx}`}>
                        {RESTRICTIONS.map((rr) => <option key={rr.value} value={rr.value}>{rr.label}</option>)}
                      </select>
                      <button type="button" className="se-btn se-btn-ghost self-center" style={{ padding: 4, color: "#C0392B", marginLeft: "auto" }} onClick={() => removeRestriction(idx)} data-testid={`vet-visit-restriction-remove-${idx}`}>
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="date" className="se-input" value={r.start_date} onChange={(e) => updateRestriction(idx, { start_date: e.target.value })} data-testid={`vet-visit-restriction-start-${idx}`} />
                      <input type="date" className="se-input" value={r.end_date} onChange={(e) => updateRestriction(idx, { end_date: e.target.value })} data-testid={`vet-visit-restriction-end-${idx}`} />
                    </div>
                    {r.restriction_type === "hand_walk" && (
                      <div className="grid grid-cols-2 gap-2" data-testid={`vet-visit-restriction-handwalk-${idx}`}>
                        <input
                          type="number" min="1" max="20"
                          className="se-input"
                          placeholder="Times per day"
                          value={r.times_per_day ?? ""}
                          onChange={(e) => updateRestriction(idx, { times_per_day: e.target.value })}
                          data-testid={`vet-visit-restriction-times-per-day-${idx}`}
                        />
                        <input
                          type="number" min="1" max="600"
                          className="se-input"
                          placeholder="Duration (minutes)"
                          value={r.duration_minutes ?? ""}
                          onChange={(e) => updateRestriction(idx, { duration_minutes: e.target.value })}
                          data-testid={`vet-visit-restriction-duration-minutes-${idx}`}
                        />
                      </div>
                    )}
                    <input className="se-input" placeholder="Notes (optional)" value={r.notes || ""} onChange={(e) => updateRestriction(idx, { notes: e.target.value })} data-testid={`vet-visit-restriction-notes-${idx}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}

        {err && <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="vet-visit-error">{err}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="vet-visit-submit"><Save size={14} /> {busy ? "Saving…" : (isEdit ? "Save changes" : "Log visit")}</button>
        </div>
      </form>
    </div>
  );
}
